import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { DropdownOption } from '~/components/Dropdown';
import { Dropdown } from '~/components/Dropdown/Dropdown';
import { cn } from '~/lib/cn';
import type { BarChartDatum } from '~/modules/artist-roster/components/BarChart/BarChart';
import { BarChart } from '~/modules/artist-roster/components/BarChart/BarChart';
import type { StreamType } from '~/modules/artist-roster/components/LineGraph/LineGraph';
import { PerformanceModuleHeader } from '~/modules/artist-roster/pages/track-details/components/PerformanceModules/PerformanceModuleHeader';
import {
  type AgeRangeData,
  genderNames,
  mockDemographicStreamingData,
} from '~/modules/artist-roster/pages/track-details/mockData/mockData';
import { formatMillionsNumber } from '~/modules/artist-roster/utils/formatMillionsNumber';

import { DemographicsTooltip } from './components/DemographicsTooltip';

export interface DemographicsTableProps {
  streamType?: StreamType;
  territory: string;
  startDate: string;
  endDate: string;
  className?: string;
}

export function DemographicsTable({
  streamType = 'streams',
  territory,
  startDate,
  endDate,
  className,
}: DemographicsTableProps) {
  const { t } = useTranslation('roster');
  const [genderIndex, setGenderIndex] = useState(0);

  // @TODO WMG1-268 Retrieve real data from streaming API call
  const genderKeys = Object.keys(
    mockDemographicStreamingData.streams_by_demographics,
  ) as (keyof typeof mockDemographicStreamingData.streams_by_demographics)[];
  const ageRangeKeys = Object.keys(
    mockDemographicStreamingData.streams_by_demographics.all,
  ) as (keyof typeof mockDemographicStreamingData.streams_by_demographics.all)[];

  ageRangeKeys.splice(ageRangeKeys.indexOf('total_streams'), 1);

  const options: DropdownOption[] = genderNames.map((gender, index) => ({
    label: gender,
    value: index.toString(),
  }));

  const demographicData =
    mockDemographicStreamingData.streams_by_demographics[
      genderKeys[genderIndex]
    ];
  const totalStreams = demographicData.total_streams;
  const barChartData: BarChartDatum[] = ageRangeKeys.map((ageRange) => {
    const ageRangeData = demographicData[ageRange] as AgeRangeData;

    let percentageValues: number[] = [];

    switch (streamType) {
      case 'streams':
        percentageValues = [
          Math.round((ageRangeData.total / totalStreams) * 100),
        ];
        break;
      case 'organicStreams':
        percentageValues = Object.values(ageRangeData.organic_streams).map(
          (value) => (value / totalStreams) * 100,
        );
        break;
      case 'programmedStreams':
      case 'benchmarkStreams':
      default:
        break;
    }

    return {
      percentageValues,
      categoryLabel: ageRange.toString(),
      valueLabel: formatMillionsNumber(ageRangeData.total),
      delta: ageRangeData.change
        ? Math.round((ageRangeData.change / totalStreams) * 100)
        : undefined,
      tooltip: {
        contentClassName: 'leading-none bg-level-03 w-[260px]',
        content: (
          <DemographicsTooltip
            {...{ ageRange, ageRangeData, totalStreams, streamType }}
          />
        ),
        side: 'bottom',
        sideOffset: 8,
        children: null,
      },
    };
  });

  return (
    <>
      <div
        className={cn(
          'flex flex-col rounded-sm border-px border-solid p-lg duration-300 ease-out bg-level-01 border-emphasis-subtle hover:shadow-xl hover:bg-level-02 hover:border-emphasis-strong',
          className,
        )}
      >
        <div className="flex items-start justify-between">
          <PerformanceModuleHeader
            header={t('trackDetails.demographic')}
            territory={territory}
            startDate={startDate}
            endDate={endDate}
          />
          <div className="w-1/4 text-caption-sm font-semibold">
            {/* @TODO WMG1-269 Replace dropdown with new variant that matches Marketing Figma */}
            <Dropdown
              onChange={(option) => {
                setGenderIndex(Number(option));
              }}
              options={options}
              placeholder="Select an option"
              value={genderIndex.toString()}
            />
          </div>
        </div>
        <BarChart data={barChartData} direction="horizontal" />
      </div>
    </>
  );
}

DemographicsTable.displayName = 'DemographicsTable';
